import { Navigate, Route, Routes } from 'react-router-dom';
import { inicioDoPapel, useSessao, type Papel } from './lib/sessao.tsx';
import { Empresas } from './paginas/Empresas.tsx';
import { Entrar } from './paginas/Entrar.tsx';
import { ExameEditor } from './paginas/ExameEditor.tsx';
import { Atendimento } from './paginas/Atendimento.tsx';
import { Fila } from './paginas/Fila.tsx';
import { Inicio } from './paginas/Inicio.tsx';
import { Impressao } from './paginas/Impressao.tsx';
import { Laudo } from './paginas/Laudo.tsx';
import { Configuracoes } from './paginas/Configuracoes.tsx';
import { Consulta } from './paginas/Consulta.tsx';
import { Documento } from './paginas/Documento.tsx';
import { Convenios, Especialidades } from './paginas/ListaSimples.tsx';
import { Medicos } from './paginas/Medicos.tsx';
import { MinhaFila } from './paginas/MinhaFila.tsx';
import { Pacientes } from './paginas/Pacientes.tsx';
import { Ocupacional } from './paginas/Ocupacional.tsx';
import { Procedimentos } from './paginas/Procedimentos.tsx';
import { Quarentena } from './paginas/Quarentena.tsx';
import { Relatorios } from './paginas/Relatorios.tsx';
import { TrocarSenha } from './paginas/TrocarSenha.tsx';
import { Usuarios } from './paginas/Usuarios.tsx';

/** Rota protegida. `papeis` limita quem entra; sem ele, vale para todos os
 *  autenticados. Quem tenta uma rota de outro papel volta para a sua própria
 *  tela inicial, e não para uma página de erro. */
function Protegida({ papeis, children }: { papeis?: Papel[]; children: React.ReactNode }) {
  const { usuario, carregando } = useSessao();
  if (carregando) return null;
  if (!usuario) return <Navigate to="/entrar" replace />;
  if (usuario.trocarSenha) return <Navigate to="/senha" replace />;
  if (papeis && !papeis.includes(usuario.papel)) {
    return <Navigate to={inicioDoPapel(usuario.papel, usuario.podeLaudar !== false)} replace />;
  }
  return <>{children}</>;
}

const CLINICA: [string, React.ReactNode][] = [
  ['/inicio', <Inicio />],
  ['/atendimento', <Atendimento />],
  ['/fila', <Fila />],
  ['/quarentena', <Quarentena />],
  ['/exames/:id', <ExameEditor />],
  ['/pacientes', <Pacientes />],
  ['/empresas', <Empresas />],
  ['/procedimentos', <Procedimentos />],
  ['/especialidades', <Especialidades />],
  ['/convenios', <Convenios />],
  ['/medicos', <Medicos />],
  ['/relatorios', <Relatorios />],
  ['/configuracoes', <Configuracoes />],
  ['/usuarios', <Usuarios />],
];

export function App() {
  const { usuario, carregando } = useSessao();
  const inicio = inicioDoPapel(usuario?.papel, usuario?.podeLaudar !== false, usuario?.admin);

  return (
    <Routes>
      <Route
        path="/entrar"
        element={carregando ? null : usuario ? <Navigate to={inicio} replace /> : <Entrar />}
      />
      <Route
        path="/senha"
        element={carregando ? null : usuario ? <TrocarSenha /> : <Navigate to="/entrar" replace />}
      />

      {CLINICA.map(([caminho, elemento]) => (
        <Route
          key={caminho}
          path={caminho}
          element={<Protegida papeis={['usuario']}>{elemento}</Protegida>}
        />
      ))}

      {/* O médico lauda; a clínica também abre a mesma tela para acompanhar. */}
      <Route
        path="/laudos/:exameId"
        element={
          <Protegida papeis={['usuario', 'medico']}>
            <Laudo />
          </Protegida>
        }
      />
      <Route
        path="/laudar"
        element={
          <Protegida papeis={['medico']}>
            <MinhaFila />
          </Protegida>
        }
      />
      <Route
        path="/meus-laudos"
        element={
          <Protegida papeis={['medico']}>
            <MinhaFila assinados />
          </Protegida>
        }
      />
      {/* Retirada dos laudos de medicina ocupacional. É usuário da clínica com
          a permissão, não um acesso separado -- por isso o papel aqui é
          'usuario' e quem filtra de fato é `pode_ocupacional`, conferida no
          servidor a cada requisição. */}
      <Route
        path="/retirar-laudos"
        element={
          <Protegida papeis={['usuario']}>
            <Ocupacional />
          </Protegida>
        }
      />

      {/* Médico que solicita mas não lauda. O servidor já recorta o que ele
          enxerga; esta tela apenas não oferece o que ele não pode fazer. */}
      <Route
        path="/meus-pedidos"
        element={
          <Protegida papeis={['medico']}>
            <MinhaFila solicitados />
          </Protegida>
        }
      />


      {/* A raiz é pública: o paciente retira o exame com o código e a data de
          nascimento. Quem já está logado vai direto para a sua área. */}
      <Route
        path="/"
        element={carregando ? null : usuario ? <Navigate to={inicio} replace /> : <Consulta />}
      />
      {/* Link curto do WhatsApp: cai na mesma tela com o código preenchido.
          Curto de propósito -- ele é digitado à mão quando alguém encaminha a
          mensagem por telefone. */}
      <Route path="/e/:codigo" element={<Consulta />} />
      {/* Link direto do aviso por WhatsApp. Abre o PDF sem pedir a data de
          nascimento -- vale por poucos dias e só para aquele documento. */}
      <Route path="/d/:token" element={<Documento />} />

      {/* Fora do Layout e sem sessão: é a página que o gerador de PDF abre. */}
      <Route path="/imprimir/:exameId" element={<Impressao />} />

      <Route path="*" element={<Navigate to={usuario ? inicio : '/'} replace />} />
    </Routes>
  );
}
